import api from "./api";
import { V2_BASE_URL, ONBOARDING_BASE } from "./bases";
import { getPracticeId } from "../config/practice";

/*
 * Patient / staff files of the onboarding and chat stores (security fix, 2026-10-15).
 *
 * These files used to load straight from public URLs on api.prd.brainymed.com:
 *   /onboarding/patient_docs/<name>      documents shared with a patient
 *   /onboarding/images/<name>            insurance card photos + signatures (intake)
 *   /onboarding/profile_pics/<name>      intake selfies
 *   /onboarding/emp_onboarding/<name>    employee face photos (clock-in face match)
 *   /onboarding/emp_timesheets/<name>    clock-in / clock-out photos
 *   /onboarding/newsfeed_files/<name>    news feed attachments
 *   /ag_chats/<name>                     chat attachments
 * with no login, so anyone holding a name could read them. Apache now refuses
 * those paths; the files come from v2 instead:
 *
 *   GET /v2/ag/v1/{pid}/files/onboarding/<same path>   (staff token, like every
 *   GET /v2/ag/v1/{pid}/files/ag_chats/<same path>      other api.js call)
 *
 * so any stored value maps 1:1: a full legacy URL, a bare stored name plus its
 * folder, or the *_url / file_url fields the API now returns. The answer is a
 * Blob; viewers get an object URL (released when they unmount) or, for the
 * react-pdf onboarding PDF, a data URL.
 *
 * Links the API signs for viewers that cannot send a header (.../files/s/<token>/
 * <name>, the getDocShare "link", employee_images, newsfeed "documents_link") open as
 * they are, without the token. They expire (30 min for patient files, 72 h
 * for employee photos), so the authenticated route is preferred whenever the
 * API also gives one (file_url, *_url, employee_image_urls).
 *
 * Pinned to v2 (`_gw: "v2-pinned"`, as the fax and forms code do): the route
 * exists on v2 only, so it skips the v1/v2 routing rules, never falls back to v1
 * and never trips the v2 circuit breaker (api/api.js).
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };

export const FOLDERS = {
  patientDocs: "patient_docs",
  images: "images",
  profilePics: "profile_pics",
  empOnboarding: "emp_onboarding",
  empTimesheets: "emp_timesheets",
  newsfeed: "newsfeed_files",
  questionnaire: "questionnarie_forms_files",
};
const FOLDER_SET = new Set(Object.values(FOLDERS));
const LEGACY_HOSTS = ["api.prd.brainymed.com"];
const ROUTE_RE = /^(?:\/v2\/ag)?\/v1\/(\d+)\/files\/(onboarding|ag_chats)\/(.+)$/;
const LEGACY_RE = /^\/(onboarding|ag_chats)\/(.+)$/;
const SIGNED_RE = /\/v1\/\d+\/files\/s\/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+(\/[^/?#]*)?$/;

const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob;
const hostOf = (u) => {
  try {
    return new URL(u).host.toLowerCase();
  } catch (e) {
    return "";
  }
};

function decodeSegment(s) {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
}

// A single stored name we are willing to put in a path (same rule as the server).
export function nameOk(name) {
  return (
    typeof name === "string" &&
    name.length > 0 &&
    name.length <= 255 &&
    !name.startsWith(".") &&
    name.trim() === name &&
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001f\u007f/\\]/.test(name)
  );
}

function encodeRel(rel) {
  const parts = rel.split("/").map(decodeSegment);
  if (parts.some((p) => p === "" || p === "." || p === "..")) return null;
  if (!nameOk(parts[parts.length - 1])) return null;
  return parts.map((p) => encodeURIComponent(p)).join("/");
}

/*
 * What a stored value is, as one of
 *   { kind: "route",  path }   authenticated v2 path (api.get with the staff token)
 *   { kind: "signed", url }    signed link (fetched without a token)
 *   { kind: "direct", url }    anything else we must load as it is (blob:/data:
 *                              previews, other hosts such as the legacy chimlw one)
 *   null                       nothing to load
 * `folder` is needed only for bare stored names (fornt_image, profile, clockin_pic...).
 */
export function patientFileRef(stored, folder, practiceId = getPracticeId()) {
  if (typeof stored !== "string" || !stored.trim()) return null;
  const value = stored.trim();
  if (/^(blob|data):/i.test(value)) return { kind: "direct", url: value };
  const looksLikeUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("/");
  if (!looksLikeUrl) {
    if (!FOLDER_SET.has(folder) || !nameOk(value) || !practiceId) return null;
    return { kind: "route", path: `/v1/${practiceId}/files/onboarding/${folder}/${encodeURIComponent(value)}` };
  }
  let url;
  try {
    url = new URL(value, "https://api.prd.brainymed.com");
  } catch (e) {
    return null;
  }
  const ourHosts = [...LEGACY_HOSTS, hostOf(V2_BASE_URL), hostOf(ONBOARDING_BASE)].filter(Boolean);
  if (!ourHosts.includes(url.host.toLowerCase())) return { kind: "direct", url: value };
  // URL() resolves dot segments ("/onboarding/x/../../etc"): refuse them before it does
  const rawPath = value.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "").split(/[?#]/)[0];
  if (rawPath.split("/").some((seg) => [".", ".."].includes(decodeSegment(seg)))) return null;
  if (SIGNED_RE.test(url.pathname)) return { kind: "signed", url: url.href };
  let m = url.pathname.match(ROUTE_RE);
  if (m) {
    const rel = encodeRel(m[3]);
    return rel ? { kind: "route", path: `/v1/${m[1]}/files/${m[2]}/${rel}` } : null;
  }
  // a configured ONBOARDING_BASE with its own path prefix (dev builds)
  const base = ONBOARDING_BASE.replace(/\/+$/, "");
  if (base && value.startsWith(`${base}/`) && !/^https?:\/\/api\.prd\.brainymed\.com\/onboarding$/.test(base)) {
    const rel = encodeRel(value.slice(base.length + 1).split(/[?#]/)[0]);
    return rel && practiceId ? { kind: "route", path: `/v1/${practiceId}/files/onboarding/${rel}` } : null;
  }
  m = url.pathname.match(LEGACY_RE);
  if (m && practiceId) {
    const rel = encodeRel(m[2]);
    return rel ? { kind: "route", path: `/v1/${practiceId}/files/${m[1]}/${rel}` } : null;
  }
  // another path on our own API host is not a file of these stores
  return null;
}

// The route URL list the API returns next to a signed list (employee_image_urls
// next to employee_images): the route entry where there is one, else the other.
export function preferRouteUrls(routeUrls, otherUrls) {
  const other = Array.isArray(otherUrls) ? otherUrls : [];
  if (!Array.isArray(routeUrls) || !routeUrls.length) return other;
  return routeUrls.map((u, i) => u || other[i]).filter(Boolean);
}

// The last path segment of a stored value, for display ("20261015_report.pdf").
export function patientFileName(stored) {
  if (typeof stored !== "string" || !stored) return "";
  if (/^(blob|data):/i.test(stored)) return "";
  const path = stored.split(/[?#]/)[0];
  return decodeSegment(path.split("/").filter(Boolean).pop() || "");
}

const fileError = (message, status) => Object.assign(new Error(message), { patientFile: true, status });

async function blobText(b) {
  if (typeof b.text === "function") return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

// The file as a Blob. Throws an Error with .status (404 not found / not yours,
// 403 outside your location or scope, 410 an expired signed link).
export async function patientFileBlob(stored, folder) {
  const ref = patientFileRef(stored, folder);
  if (!ref) throw fileError("File not available", 404);
  let data;
  if (ref.kind === "route") {
    try {
      // api.js resolves to the response body: the Blob itself.
      data = await api.get(ref.path, { ...V2, responseType: "blob" });
    } catch (e) {
      throw fileError("File not available", (e && e.response && e.response.status) || 0);
    }
  } else {
    const res = await fetch(ref.url, { credentials: "omit", referrerPolicy: "no-referrer" });
    if (!res.ok) throw fileError(res.status === 410 ? "This link has expired" : "File not available", res.status);
    data = await res.blob();
  }
  if (!isBlob(data) || !data.size) throw fileError("File not available", 404);
  if (/json/i.test(data.type || "")) {
    let message = "File not available";
    try {
      const body = JSON.parse(await blobText(data));
      if (body && typeof body.message === "string") message = body.message;
    } catch (e) {
      // keep the generic message
    }
    throw fileError(message, 404);
  }
  return data;
}

export async function patientFileObjectUrl(stored, folder) {
  return URL.createObjectURL(await patientFileBlob(stored, folder));
}

// data: URL (react-pdf's <Image> takes PNG / JPEG data URLs).
export async function patientFileDataUrl(stored, folder) {
  const blob = await patientFileBlob(stored, folder);
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/*
 * Open a file in a new tab. The tab is opened at once (inside the click, so no
 * popup blocker), then pointed at the object URL; the URL is released after a
 * minute. A value that is not one of our files opens as it is. Throws (and
 * closes the blank tab) when the file cannot be loaded.
 */
export async function openPatientFile(stored, folder) {
  const ref = patientFileRef(stored, folder);
  if (!ref) throw fileError("File not available", 404);
  if (ref.kind === "direct") {
    window.open(ref.url, "_blank", "noopener,noreferrer");
    return;
  }
  const tab = window.open("about:blank", "_blank");
  try {
    const url = await patientFileObjectUrl(stored, folder);
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}
